import { useState, useEffect, useRef } from 'react';
import { API_BASE_URL } from '../../config';
import { DB } from '../../utils/indexedDB.js';

const useUserWorks = (uid, token) => {
    const [UserWorks, setUserWorks] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const mountedRef = useRef(false);
    const requestIdRef = useRef(0);

    useEffect(() => {
        mountedRef.current = true;
        const controller = new AbortController();
        const signal = controller.signal;
        const currentRequestId = ++requestIdRef.current;

        const fetchUserWorks = async () => {
            try {
                if (!uid || !token) {
                    setLoading(false);
                    return;
                }

                const response = await fetch(`${API_BASE_URL}/works/for/user/${uid}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                    signal,
                });

                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const data = await response.json();
                // Only update state if this is still the most recent request
                if (mountedRef.current && currentRequestId === requestIdRef.current) {
                    setUserWorks(data);
                    DB.setItem('UserWorks', data);
                }
            } catch (err) {
                if (err.name !== 'AbortError' && mountedRef.current && currentRequestId === requestIdRef.current) {
                    setError(err);
                }
            } finally {
                if (mountedRef.current && currentRequestId === requestIdRef.current) {
                    setLoading(false);
                }
            }
        };

        const loadData = async () => {
            if (navigator.onLine && token && uid) {
                await fetchUserWorks();
            } else {
                const cachedUserWorks = await DB.getItem('UserWorks');
                if (cachedUserWorks && mountedRef.current && currentRequestId === requestIdRef.current) {
                    setUserWorks(cachedUserWorks);
                }
                if (mountedRef.current && currentRequestId === requestIdRef.current) {
                    setLoading(false);
                }
            }
        };

        loadData();

        return () => {
            mountedRef.current = false;
            controller.abort();
        };
    }, [uid, token]);

    return { UserWorks, loading, error };
};

export default useUserWorks;
